import React, { useState, useEffect, useCallback } from 'react';
import { useApp } from '../context/AppContext';
import { getEmpleados, getCuentas } from '../services/api';
import api from '../services/api';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Banknote, Plus, Trash2, Search, Calendar, Pencil, Eye, XCircle, FileText } from 'lucide-react';

const MEDIOS_PAGO = [
  { value: 'EFECTIVO', label: 'Efectivo' },
  { value: 'TRANSFERENCIA', label: 'Transferencia' },
  { value: 'YAPE', label: 'Yape' },
  { value: 'PLIN', label: 'Plin' },
];

const AdelantosPage = () => {
  const { empresaActual } = useApp();
  const [adelantos, setAdelantos] = useState([]);
  const [empleados, setEmpleados] = useState([]);
  const [cuentas, setCuentas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtroEstado, setFiltroEstado] = useState('PENDIENTE'); // Por defecto mostrar PENDIENTES
  const [busqueda, setBusqueda] = useState('');
  
  // Modal
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null); // null = nuevo, número = editando
  const [formData, setFormData] = useState({
    empleado_id: '',
    fecha: new Date().toISOString().split('T')[0],
    monto: '',
    cuenta_id: '',
    medio_pago: 'EFECTIVO',
    nota: ''
  });
  const [saving, setSaving] = useState(false);
  
  // Modal anulación
  const [anularModalOpen, setAnularModalOpen] = useState(false);
  const [adelantoAnular, setAdelantoAnular] = useState(null);
  
  // Modal ver detalle (para aplicados)
  const [detalleModalOpen, setDetalleModalOpen] = useState(false);
  const [adelantoDetalle, setAdelantoDetalle] = useState(null);

  const loadData = useCallback(async () => {
    if (!empresaActual?.id) return;
    
    setLoading(true);
    try {
      const params = { empresa_id: empresaActual.id };
      // Solo agregar estado si no es "all"
      if (filtroEstado && filtroEstado !== 'all') {
        params.estado = filtroEstado;
      }
      const [adelantosRes, empleadosRes, cuentasRes] = await Promise.all([
        api.get('/adelantos', { params }),
        getEmpleados(empresaActual.id, true),
        getCuentas(empresaActual.id)
      ]);
      setAdelantos(adelantosRes.data);
      setEmpleados(empleadosRes.data);
      setCuentas(cuentasRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  }, [empresaActual?.id, filtroEstado]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleNew = () => {
    setEditingId(null);
    setFormData({
      empleado_id: '',
      fecha: new Date().toISOString().split('T')[0],
      monto: '',
      cuenta_id: cuentas.length > 0 ? cuentas[0].id.toString() : '',
      medio_pago: 'EFECTIVO',
      nota: ''
    });
    setModalOpen(true);
  };

  const handleEdit = (adelanto) => {
    setEditingId(adelanto.id);
    setFormData({
      empleado_id: adelanto.trabajador_id?.toString() || '',
      fecha: adelanto.fecha || new Date().toISOString().split('T')[0],
      monto: adelanto.monto_pen?.toString() || '',
      cuenta_id: adelanto.cuenta_id?.toString() || '',
      medio_pago: 'EFECTIVO', // No tenemos este campo en adelanto, default
      nota: '' // La nota está en el pago, no en el adelanto
    });
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.empleado_id || !formData.monto || !formData.cuenta_id) {
      toast.error('Complete los campos obligatorios');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        empresa_id: empresaActual.id,
        empleado_id: parseInt(formData.empleado_id),
        fecha: formData.fecha,
        monto: parseFloat(formData.monto),
        cuenta_id: parseInt(formData.cuenta_id),
        medio_pago: formData.medio_pago,
        nota: formData.nota
      };

      if (editingId) {
        // Editar existente
        await api.put(`/adelantos/${editingId}`, payload);
        toast.success('Adelanto actualizado correctamente');
      } else {
        // Crear nuevo
        await api.post('/adelantos', payload);
        toast.success('Adelanto registrado correctamente');
      }
      
      setModalOpen(false);
      setEditingId(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleAnular = (adelanto) => {
    setAdelantoAnular(adelanto);
    setAnularModalOpen(true);
  };

  const confirmarAnular = async () => {
    if (!adelantoAnular) return;
    try {
      await api.delete(`/adelantos/${adelantoAnular.id}`);
      toast.success('Adelanto anulado correctamente');
      setAnularModalOpen(false);
      setAdelantoAnular(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al anular');
    }
  };

  const handleVerDetalle = (adelanto) => {
    setAdelantoDetalle(adelanto);
    setDetalleModalOpen(true);
  };

  // Filtrar adelantos
  const adelantosFiltrados = adelantos.filter(a => {
    const matchBusqueda = !busqueda || 
      a.nombres?.toLowerCase().includes(busqueda.toLowerCase()) ||
      a.apellido_paterno?.toLowerCase().includes(busqueda.toLowerCase()) ||
      a.nro_doc?.includes(busqueda);
    return matchBusqueda;
  });

  // Estadísticas
  const stats = {
    total: adelantos.length,
    pendientes: adelantos.filter(a => a.estado === 'PENDIENTE').length,
    montoPendiente: adelantos.filter(a => a.estado === 'PENDIENTE').reduce((s, a) => s + (a.monto_pen || 0), 0)
  };

  return (
    <div className="space-y-6" data-testid="adelantos-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Banknote className="w-7 h-7 text-amber-600" />
            Adelantos de Sueldo
          </h1>
          <p className="text-gray-500 text-sm mt-1">Registro de adelantos a empleados</p>
        </div>
        <Button onClick={handleNew} className="bg-amber-600 hover:bg-amber-700" data-testid="nuevo-adelanto-btn">
          <Plus className="w-4 h-4 mr-2" />
          Nuevo Adelanto
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-amber-50 to-white border-amber-200">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-amber-100 rounded-full">
              <Banknote className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Registros</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-orange-50 to-white border-orange-200">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-orange-100 rounded-full">
              <Calendar className="w-6 h-6 text-orange-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Pendientes</p>
              <p className="text-2xl font-bold text-orange-600">{stats.pendientes}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-red-50 to-white border-red-200">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-red-100 rounded-full">
              <Banknote className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Monto Pendiente</p>
              <p className="text-2xl font-bold text-red-600">S/. {stats.montoPendiente.toLocaleString('es-PE', { minimumFractionDigits: 2 })}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Buscar por nombre o documento..."
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={filtroEstado} onValueChange={setFiltroEstado}>
              <SelectTrigger className="w-[180px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="PENDIENTE">Pendientes</SelectItem>
                <SelectItem value="APLICADO">Aplicados</SelectItem>
                <SelectItem value="ANULADO">Anulados</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Tabla */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Cargando...</div>
          ) : adelantosFiltrados.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Banknote className="w-12 h-12 mx-auto mb-2 text-gray-300" />
              <p>No hay adelantos registrados</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Empleado</TableHead>
                  <TableHead className="text-right">Monto</TableHead>
                  <TableHead>Cuenta/Medio</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Aplicado en</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {adelantosFiltrados.map((a) => (
                  <TableRow key={a.id} className={a.estado === 'ANULADO' ? 'opacity-50' : ''}>
                    <TableCell className="font-mono">{a.fecha}</TableCell>
                    <TableCell>
                      <div className="font-medium">{a.apellido_paterno} {a.apellido_materno}</div>
                      <div className="text-sm text-gray-500">{a.nombres} - {a.nro_doc}</div>
                    </TableCell>
                    <TableCell className="text-right font-mono font-bold text-amber-600">
                      S/. {a.monto_pen?.toLocaleString('es-PE', { minimumFractionDigits: 2 })}
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">{a.cuenta_nombre || '-'}</div>
                      {a.nota_pago && <div className="text-xs text-gray-400 truncate max-w-[150px]">{a.nota_pago}</div>}
                    </TableCell>
                    <TableCell>
                      <Badge className={
                        a.estado === 'PENDIENTE' ? 'bg-amber-100 text-amber-700' :
                        a.estado === 'APLICADO' ? 'bg-emerald-100 text-emerald-700' :
                        'bg-gray-100 text-gray-600'
                      }>
                        {a.estado}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {a.estado === 'APLICADO' && a.planilla_aplicada_nombre ? (
                        <button 
                          onClick={() => handleVerDetalle(a)}
                          className="text-sm text-indigo-600 hover:text-indigo-800 hover:underline flex items-center gap-1"
                        >
                          <FileText className="w-3 h-3" />
                          {a.planilla_aplicada_nombre}
                        </button>
                      ) : a.estado === 'APLICADO' ? (
                        <span className="text-sm text-gray-400">Planilla</span>
                      ) : (
                        <span className="text-sm text-gray-300">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {a.estado === 'PENDIENTE' && (
                          <Button variant="ghost" size="sm" onClick={() => handleEdit(a)} title="Editar">
                            <Pencil className="w-4 h-4 text-blue-500" />
                          </Button>
                        )}
                        {a.estado === 'APLICADO' && (
                          <Button variant="ghost" size="sm" onClick={() => handleVerDetalle(a)} title="Ver detalle">
                            <Eye className="w-4 h-4 text-indigo-500" />
                          </Button>
                        )}
                        {a.estado !== 'ANULADO' && (
                          <Button variant="ghost" size="sm" onClick={() => handleAnular(a)} title="Anular">
                            <XCircle className="w-4 h-4 text-red-500" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Modal Nuevo/Editar Adelanto */}
      <Dialog open={modalOpen} onOpenChange={(open) => { setModalOpen(open); if (!open) setEditingId(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {editingId ? <Pencil className="w-5 h-5 text-blue-600" /> : <Banknote className="w-5 h-5 text-amber-600" />}
              {editingId ? 'Editar Adelanto' : 'Nuevo Adelanto'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div>
              <Label>Empleado *</Label>
              <Select value={formData.empleado_id} onValueChange={(v) => setFormData({...formData, empleado_id: v})}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Seleccionar empleado..." />
                </SelectTrigger>
                <SelectContent>
                  {empleados.map(e => (
                    <SelectItem key={e.id} value={e.id.toString()}>
                      {e.apellido_paterno} {e.apellido_materno}, {e.nombres}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Fecha *</Label>
                <Input 
                  type="date"
                  value={formData.fecha} 
                  onChange={(e) => setFormData({...formData, fecha: e.target.value})}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Monto (S/.) *</Label>
                <Input 
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.monto} 
                  onChange={(e) => setFormData({...formData, monto: e.target.value})}
                  className="mt-1 text-right"
                  placeholder="0.00"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Cuenta de Pago *</Label>
                <Select value={formData.cuenta_id} onValueChange={(v) => setFormData({...formData, cuenta_id: v})}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent>
                    {cuentas.map(c => (
                      <SelectItem key={c.id} value={c.id.toString()}>
                        {c.nombre}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Medio de Pago</Label>
                <Select value={formData.medio_pago} onValueChange={(v) => setFormData({...formData, medio_pago: v})}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MEDIOS_PAGO.map(m => (
                      <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Nota (opcional)</Label>
              <Input 
                value={formData.nota} 
                onChange={(e) => setFormData({...formData, nota: e.target.value})}
                className="mt-1"
                placeholder="Motivo del adelanto..."
              />
            </div>

            <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
              <p className="text-sm text-amber-700">
                <strong>Nota:</strong> {editingId 
                  ? 'Al actualizar el adelanto se actualizará también el egreso asociado.'
                  : 'Al registrar el adelanto se generará automáticamente un egreso de la cuenta seleccionada.'}
              </p>
            </div>
          </div>

          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => { setModalOpen(false); setEditingId(null); }}>Cancelar</Button>
            <Button onClick={handleSubmit} disabled={saving} className={editingId ? "bg-blue-600 hover:bg-blue-700" : "bg-amber-600 hover:bg-amber-700"}>
              {saving ? 'Guardando...' : (editingId ? 'Actualizar' : 'Registrar Adelanto')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Anular Adelanto */}
      <AlertDialog open={anularModalOpen} onOpenChange={setAnularModalOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <XCircle className="w-5 h-5" />
              ¿Anular este adelanto?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              {adelantoAnular?.estado === 'APLICADO' ? (
                <p className="text-amber-600 font-medium">
                  ⚠️ Este adelanto ya fue APLICADO a una planilla. Al anularlo, el monto ya no se descontará en futuras planillas.
                </p>
              ) : (
                <p>Se revertirá el pago asociado a este adelanto.</p>
              )}
              {adelantoAnular && (
                <div className="bg-gray-50 p-3 rounded-lg mt-3 text-sm">
                  <p><strong>Empleado:</strong> {adelantoAnular.apellido_paterno} {adelantoAnular.apellido_materno}, {adelantoAnular.nombres}</p>
                  <p><strong>Fecha:</strong> {adelantoAnular.fecha}</p>
                  <p><strong>Monto:</strong> S/. {adelantoAnular.monto_pen?.toLocaleString('es-PE', { minimumFractionDigits: 2 })}</p>
                  <p><strong>Estado actual:</strong> {adelantoAnular.estado}</p>
                  {adelantoAnular.planilla_aplicada_nombre && (
                    <p><strong>Aplicado en:</strong> {adelantoAnular.planilla_aplicada_nombre}</p>
                  )}
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setAdelantoAnular(null)}>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmarAnular}
              className="bg-red-600 hover:bg-red-700"
            >
              Sí, anular adelanto
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Modal Ver Detalle Adelanto Aplicado */}
      <Dialog open={detalleModalOpen} onOpenChange={setDetalleModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-indigo-600" />
              Detalle del Adelanto
            </DialogTitle>
          </DialogHeader>
          {adelantoDetalle && (
            <div className="space-y-4 mt-4">
              <div className="bg-gradient-to-br from-indigo-50 to-white p-4 rounded-lg border border-indigo-200">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-gray-500">Empleado:</span>
                    <p className="font-medium">{adelantoDetalle.apellido_paterno} {adelantoDetalle.apellido_materno}, {adelantoDetalle.nombres}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Documento:</span>
                    <p className="font-medium">{adelantoDetalle.nro_doc}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Fecha Adelanto:</span>
                    <p className="font-medium">{adelantoDetalle.fecha}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Monto:</span>
                    <p className="font-bold text-amber-600">S/. {adelantoDetalle.monto_pen?.toLocaleString('es-PE', { minimumFractionDigits: 2 })}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Cuenta de Pago:</span>
                    <p className="font-medium">{adelantoDetalle.cuenta_nombre || '-'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Estado:</span>
                    <Badge className={
                      adelantoDetalle.estado === 'PENDIENTE' ? 'bg-amber-100 text-amber-700' :
                      adelantoDetalle.estado === 'APLICADO' ? 'bg-emerald-100 text-emerald-700' :
                      'bg-gray-100 text-gray-600'
                    }>
                      {adelantoDetalle.estado}
                    </Badge>
                  </div>
                </div>
              </div>
              
              {adelantoDetalle.estado === 'APLICADO' && (
                <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200">
                  <h4 className="font-medium text-emerald-800 flex items-center gap-2 mb-2">
                    <FileText className="w-4 h-4" />
                    Aplicado en Planilla
                  </h4>
                  <p className="text-emerald-700 font-medium">
                    {adelantoDetalle.planilla_aplicada_nombre || 'Planilla (ver módulo Planilla)'}
                  </p>
                  {adelantoDetalle.planilla_aplicada_id && (
                    <p className="text-sm text-emerald-600 mt-1">
                      ID Planilla: #{adelantoDetalle.planilla_aplicada_id}
                    </p>
                  )}
                </div>
              )}

              {adelantoDetalle.nota_pago && (
                <div className="bg-gray-50 p-3 rounded-lg">
                  <span className="text-sm text-gray-500">Nota del pago:</span>
                  <p className="text-sm">{adelantoDetalle.nota_pago}</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setDetalleModalOpen(false)}>Cerrar</Button>
            {adelantoDetalle?.estado !== 'ANULADO' && (
              <Button 
                variant="destructive" 
                onClick={() => {
                  setDetalleModalOpen(false);
                  handleAnular(adelantoDetalle);
                }}
              >
                <XCircle className="w-4 h-4 mr-2" />
                Anular
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdelantosPage;
